/**
 * The append-only task history as a timeline, and the localised absolute
 * timestamp it and the task metadata render.
 */

import { useMemo, type ReactNode } from 'react';

import { useLanguage } from '../../i18n/language.js';
import type { TaskEventRecord } from '../api.js';
import { useWorkspaceCopy } from './use-workspace-copy.js';

/** An absolute timestamp, localised, with the raw ISO string as the title. */
export function EventTime({ iso }: { readonly iso: string }): ReactNode {
  const { language } = useLanguage();
  const formatted = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language === 'ar' ? 'ar' : 'en', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  }, [iso, language]);

  return (
    <time
      dateTime={iso}
      title={iso}
      style={{ font: 'var(--type-body-tabular)', color: 'var(--text-muted)' }}
    >
      {formatted}
    </time>
  );
}

export function ActivityTimeline({
  events,
}: {
  readonly events: readonly TaskEventRecord[];
}): ReactNode {
  const copy = useWorkspaceCopy();

  return (
    <ol className="mj-timeline">
      {events.map((event) => (
        <li key={event.id} className="mj-timeline-entry">
          <span aria-hidden="true" className="mj-timeline-dot" />
          <div>
            <p style={{ font: 'var(--type-body-md)', color: 'var(--text-primary)' }}>
              <strong>{copy.events[event.kind] ?? event.kind}</strong>
              {' · '}
              {event.actor.displayName}
            </p>
            <EventTime iso={event.occurredAt} />
            {event.note !== null ? (
              <p
                style={{
                  font: 'var(--type-body-md)',
                  color: 'var(--text-secondary)',
                  marginTop: 4,
                  borderInlineStart: '2px solid var(--border-hairline)',
                  paddingInlineStart: 10,
                }}
              >
                {event.note}
              </p>
            ) : null}
            {event.detail !== null && typeof event.detail['url'] === 'string' ? (
              <p style={{ marginTop: 4 }}>
                <a
                  className="mj-ltr"
                  dir="ltr"
                  href={event.detail['url']}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    font: 'var(--type-body-tabular)',
                    color: 'var(--text-link)',
                  }}
                >
                  {event.detail['url']}
                </a>
              </p>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}
